import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

// Real Application Imports
import { useToast } from '../../contexts/ToastContext';
import ListingCard from '../../components/ui/ListingCard';
import ImageCropModal from './ImageCropModal';

// --- Real Storage Uploader for Storefront ---
function StorefrontImageUpload({ onUploadComplete, storage, userId, path, isFullWidth, aspectRatio = 1 }) {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [fileId] = useState(() => Math.random().toString(36).substring(7));
    
    // NEW: State to hold the file selected by the user while they crop it
    const [fileToCrop, setFileToCrop] = useState(null);
    
    // Step 1: User selects a file
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFileToCrop(file); // Open modal instead of uploading immediately
        }
        e.target.value = ''; // Reset input
    };
    
    // Step 2: The crop modal returns the modified file, NOW we upload it
    const executeUpload = async (fileToUpload) => {
        setFileToCrop(null); // Close the modal
        if (!fileToUpload || !storage || !userId) return;
        
        setUploading(true);
        setProgress(0);
        
        try {
            const fileRef = ref(storage, `storefronts/${userId}/${path}/${Date.now()}_${fileToUpload.name}`);
            const uploadTask = uploadBytesResumable(fileRef, fileToUpload);

            uploadTask.on('state_changed', 
                (snapshot) => {
                    const currentProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setProgress(Math.round(currentProgress));
                }, 
                (err) => {
                    console.error("Upload failed", err);
                    alert("Image upload failed. Check your connection.");
                    setUploading(false);
                }, 
                async () => {
                    const url = await getDownloadURL(uploadTask.snapshot.ref);
                    onUploadComplete(url);
                    setUploading(false);
                }
            );
        } catch (err) {
            console.error("Upload init failed", err);
            setUploading(false);
        }
    };

    return (
        <>
            <div className={`relative ${isFullWidth ? 'w-full h-full' : ''}`}>
                <input type="file" id={`upload-${fileId}`} className="hidden" accept="image/*" onChange={handleFileSelect} disabled={uploading} />
                <label htmlFor={`upload-${fileId}`} className={`cursor-pointer flex flex-col items-center justify-center bg-gray-100 border border-gray-300 text-gray-700 px-3 py-1.5 rounded text-xs font-bold hover:bg-gray-200 transition shadow-sm ${uploading ? 'opacity-80 pointer-events-none' : ''} ${isFullWidth ? 'w-full h-full py-2' : 'min-w-[120px]'}`}>
                    {uploading ? (
                        <div className="w-full flex flex-col items-center px-1">
                            <span className="mb-1 text-[10px]">⏳ {progress}%</span>
                            <div className="w-full bg-gray-300 rounded-full h-1.5">
                                <div className="bg-pink-600 h-1.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                            </div>
                        </div>
                    ) : '📁 Upload Image'}
                </label>
            </div>
            
            {/* Render Crop Modal if a file is selected */}
            {fileToCrop && (
                <ImageCropModal 
                    file={fileToCrop} 
                    aspectRatio={aspectRatio} 
                    onClose={() => setFileToCrop(null)} 
                    onCrop={executeUpload} 
                />
            )}
        </>
    );
}

// --- Real Storage Uploader for Storefront Video ---
function StorefrontVideoUpload({ onUploadComplete, storage, userId, path, isFullWidth }) {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [fileId] = useState(() => Math.random().toString(36).substring(7));
    
    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !storage || !userId) return;
        setUploading(true);
        setProgress(0);

        try {
            const fileRef = ref(storage, `storefronts/${userId}/${path}/${Date.now()}_${file.name}`);
            const uploadTask = uploadBytesResumable(fileRef, file);

            uploadTask.on('state_changed', 
                (snapshot) => {
                    const currentProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setProgress(Math.round(currentProgress));
                }, 
                (err) => {
                    console.error("Upload failed", err);
                    alert("Video upload failed. Check your connection.");
                    setUploading(false);
                    e.target.value = ''; 
                }, 
                async () => {
                    const url = await getDownloadURL(uploadTask.snapshot.ref);
                    onUploadComplete(url);
                    setUploading(false);
                    e.target.value = ''; 
                }
            );
        } catch (err) {
            console.error("Upload init failed", err);
            setUploading(false);
            e.target.value = ''; 
        }
    };

    return (
        <div className={`relative ${isFullWidth ? 'w-full h-full' : ''}`}>
            <input type="file" id={`upload-${fileId}`} className="hidden" accept="video/mp4,video/webm" onChange={handleUpload} disabled={uploading} />
            <label htmlFor={`upload-${fileId}`} className={`cursor-pointer flex flex-col items-center justify-center bg-gray-100 border border-gray-300 text-gray-700 px-3 py-1.5 rounded text-xs font-bold hover:bg-gray-200 transition shadow-sm ${uploading ? 'opacity-80 pointer-events-none' : ''} ${isFullWidth ? 'w-full h-full py-2' : 'min-w-[120px]'}`}>
                {uploading ? (
                    <div className="w-full flex flex-col items-center px-1">
                        <span className="mb-1 text-[10px]">⏳ {progress}%</span>
                        <div className="w-full bg-gray-300 rounded-full h-1.5">
                            <div className="bg-pink-600 h-1.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                        </div>
                    </div>
                ) : '🎬 Upload Video'}
            </label>
        </div>
    );
}

// --- Storefront Carousel Section (Editor UI) ---
function StorefrontCarouselSection({ images = [], onUpdateImages, storage, userId }) {
    const handleAddImage = (newUrl) => {
        onUpdateImages([...images, newUrl]);
    };

    const handleRemoveImage = (indexToRemove) => {
        const updatedImages = images.filter((_, index) => index !== indexToRemove);
        onUpdateImages(updatedImages);
    };

    return (
        <div className="w-full bg-white p-5 rounded-xl border border-gray-200 shadow-sm mt-4 mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-1">Manage Carousel Images</h3>
            <p className="text-xs text-gray-500 mb-4">Upload multiple images to create a sliding gallery on your storefront.</p>
            
            <div className="grid grid-cols-2 gap-4">
                {/* Existing Images */}
                {images.map((imgUrl, index) => (
                    <div key={index} className="relative group aspect-[4/5] bg-gray-100 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                        <img src={imgUrl} alt={`Carousel slot ${index + 1}`} className="w-full h-full object-cover" />
                        
                        {/* Overlay to make delete button pop out more */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300"></div>
                        
                        {/* Remove Button */}
                        <button 
                            onClick={() => handleRemoveImage(index)}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-md hover:bg-red-600 hover:scale-110 z-10"
                            title="Remove Image"
                        >
                            ✕
                        </button>
                    </div>
                ))}
                
                {/* Upload New Image Slot */}
                <div className="aspect-[4/5] bg-slate-50 rounded-lg border-2 border-dashed border-slate-300 hover:border-pink-400 hover:bg-pink-50/50 transition-all duration-300 flex items-center justify-center p-2">
                    <StorefrontImageUpload 
                        onUploadComplete={handleAddImage} 
                        storage={storage} 
                        userId={userId} 
                        path="carousel" 
                        isFullWidth={true} 
                    />
                </div>
            </div>
        </div>
    );
}

// ==========================================
// INTERNAL HELPER COMPONENTS
// ==========================================

function IFramePreview({ children, className, style }) {
    const [mountNode, setMountNode] = useState(null);
    const handleLoad = (e) => {
        const contentWindow = e.target.contentWindow;
        if (contentWindow && contentWindow.document) {
            const doc = contentWindow.document;
            Array.from(document.querySelectorAll('style, link[rel="stylesheet"]')).forEach(tag => {
                doc.head.appendChild(tag.cloneNode(true));
            });
            setMountNode(doc.body);
        }
    };
    return (
        <>
            <iframe
                title="Mobile Preview"
                onLoad={handleLoad}
                className={className}
                style={style}
                srcDoc="<!DOCTYPE html><html><head><meta name='viewport' content='width=device-width, initial-scale=1.0'><script src='https://cdn.tailwindcss.com'></script></head><body style='margin:0; padding:0; overflow-x:hidden;'></body></html>"
            />
            {mountNode && createPortal(children, mountNode)}
        </>
    );
}

function FaqAccordion({ faq }) {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="border-b border-gray-200/20">
            <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center text-left py-4 hover:opacity-80 transition">
                <span className="font-semibold">{faq.question}</span>
                <span className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>▼</span>
            </button>
            {isOpen && <div className="pb-4 opacity-80 leading-relaxed">{faq.answer}</div>}
        </div>
    );
}

// ==========================================
// RENDERER COMPONENT
// ==========================================

export function StorefrontSectionRenderer({ section, listings = [], reviews = [], user, navigateTo, vendor, startChat, theme }) {
    if (!section || !section.content) return null; 
    
    const { type, content } = section;

    const globalTheme = theme || vendor?.storefront?.theme || { 
        primaryColor: vendor?.themeColor || '#ec4899', 
        backgroundColor: '#ffffff', 
        fontFamily: vendor?.storefront?.fontFamily || 'inherit', 
        borderRadius: '0.5rem',
        buttonStyle: 'solid'
    };

    // Handle background images and parallax for any section
    const sectionStyle = {
        backgroundColor: content.backgroundColor || globalTheme.backgroundColor,
        color: content.textColor || (globalTheme.backgroundColor === '#111827' ? '#ffffff' : '#1f2937'),
        ...(content.bgImageUrl ? {
            backgroundImage: `linear-gradient(rgba(0,0,0,${content.bgOverlayOpacity || 0}), rgba(0,0,0,${content.bgOverlayOpacity || 0})), url(${content.bgImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: content.bgParallax ? 'fixed' : 'scroll'
        } : {})
    };
    
    const paddingClass = content.padding || 'py-16';
    const radius = globalTheme.borderRadius;

    // Centralized button styling based on theme preference
    const btnClass = globalTheme.buttonStyle === 'outline' 
        ? "border-2 bg-transparent hover:bg-black/5 transition shadow-sm hover:-translate-y-1 px-8 py-3 font-bold text-lg" 
        : "text-white transition shadow-lg hover:-translate-y-1 px-8 py-3 font-bold text-lg";
    const btnStyle = globalTheme.buttonStyle === 'outline'
        ? { borderColor: globalTheme.primaryColor, color: globalTheme.primaryColor, borderRadius: radius }
        : { backgroundColor: globalTheme.primaryColor, borderRadius: radius };

    switch (type) {
        case 'hero':
            return (
                <div className={`relative flex items-center ${content.alignment === 'left' ? 'justify-start' : content.alignment === 'right' ? 'justify-end' : 'justify-center'} text-center overflow-hidden`} style={{ height: content.height || '600px', backgroundColor: content.backgroundColor || globalTheme.backgroundColor }}>
                    {content.bgVideoUrl ? (
                        <video 
                            autoPlay 
                            loop 
                            muted 
                            playsInline 
                            className="absolute inset-0 w-full h-full object-cover z-0"
                        >
                            <source src={content.bgVideoUrl} type="video/mp4" />
                        </video>
                    ) : (
                        <img 
                            src={content.imageUrl || 'https://placehold.co/1920x600/1a1a1a/333333?text=Hero+Image'} 
                            alt="Hero" 
                            className="absolute inset-0 w-full h-full object-cover z-0"
                        />
                    )}
                    <div className="absolute inset-0 bg-black z-0" style={{ opacity: content.overlayOpacity !== undefined ? content.overlayOpacity : 0.4 }}></div>
                    <div className={`relative z-10 p-6 max-w-4xl mx-auto animate-fade-in-up ${content.alignment === 'left' ? 'text-left ml-0 md:ml-12' : content.alignment === 'right' ? 'text-right mr-0 md:mr-12' : 'text-center'}`}>
                        <h1 className="text-4xl md:text-6xl font-bold mb-4 drop-shadow-lg" style={{ color: content.textColor || '#ffffff' }}>{content.title}</h1>
                        <p className="text-lg md:text-2xl mb-8 drop-shadow-md" style={{ color: content.textColor || '#e5e7eb', opacity: 0.9 }}>{content.subtitle}</p>
                        {content.buttonText && (
                            <button 
                                onClick={content.buttonAction === 'chat' ? startChat : () => document.getElementById('inventory-section')?.scrollIntoView({ behavior: 'smooth' })}
                                className={btnClass}
                                style={btnStyle}
                            >
                                {content.buttonText}
                            </button>
                        )}
                    </div>
                </div>
            );
        case 'text-image':
            return (
                <div className={`${paddingClass}`} style={sectionStyle}>
                    <div className="container mx-auto px-6 max-w-6xl">
                        <div className={`flex flex-col md:flex-row items-center gap-12 ${content.layout === 'right' ? 'md:flex-row-reverse' : ''}`}>
                            <div className="flex-1">
                                <img 
                                    src={content.imageUrl || 'https://placehold.co/600x400/e2e8f0/64748b?text=Image'} 
                                    alt="About" 
                                    className="shadow-xl w-full h-auto object-cover aspect-[4/3]"
                                    style={{ borderRadius: radius }}
                                />
                            </div>
                            <div className="flex-1 space-y-6">
                                <h2 className="text-3xl md:text-4xl font-bold" style={{ color: content.textColor ? 'inherit' : globalTheme.primaryColor }}>{content.heading}</h2>
                                <p className="text-lg leading-relaxed whitespace-pre-wrap opacity-90">{content.text}</p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        case 'features':
            const featCols = content.columns === '2' ? 'md:grid-cols-2' : content.columns === '4' ? 'md:grid-cols-4' : 'md:grid-cols-3';
            return (
                <div className={`${paddingClass}`} style={sectionStyle}>
                    <div className="container mx-auto px-6 max-w-6xl text-center">
                        <h2 className="text-3xl font-bold mb-12" style={{ color: content.textColor ? 'inherit' : globalTheme.primaryColor }}>{content.heading || "Why Choose Us"}</h2>
                        <div className={`grid grid-cols-1 ${featCols} gap-8`}>
                            {(content.items || []).map((item, i) => (
                                <div key={i} className="bg-white/5 p-8 shadow-sm border border-gray-200/50 hover:shadow-md transition backdrop-blur-sm" style={{ borderRadius: radius }}>
                                    <div className="text-4xl mb-4">{item.icon || '✨'}</div>
                                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                                    <p className="opacity-80">{item.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );
        case 'inventory':
            return (
                <div id="inventory-section" className={`${paddingClass}`} style={sectionStyle}>
                    <div className="container mx-auto px-6 max-w-7xl">
                        <h2 className="text-3xl font-bold text-center mb-10" style={{ color: content.textColor ? 'inherit' : globalTheme.primaryColor }}>{content.heading || "Our Collection"}</h2>
                        {listings && listings.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 text-left text-gray-900">
                                {listings.map(listing => (
                                    <ListingCard 
                                        key={listing.id} 
                                        listing={listing} 
                                        navigateTo={navigateTo} 
                                        user={user} 
                                        isFavorite={user && !user.isAnonymous && user.favorites?.includes(listing.id)} 
                                    />
                                ))}
                            </div>
                        ) : (
                            <p className="text-center opacity-70">No items available yet.</p>
                        )}
                    </div>
                </div>
            );
        case 'gallery':
            const galCols = content.columns === '2' ? 'md:grid-cols-2' : content.columns === '3' ? 'md:grid-cols-3' : 'md:grid-cols-4';
            const aspectClass = content.aspectRatio === 'video' ? 'aspect-video' : content.aspectRatio === 'portrait' ? 'aspect-[3/4]' : 'aspect-square';
            return (
                <div className={`${paddingClass}`} style={sectionStyle}>
                    <div className="container mx-auto px-6 max-w-7xl">
                        <h2 className="text-3xl font-bold text-center mb-10" style={{ color: content.textColor ? 'inherit' : globalTheme.primaryColor }}>{content.heading || "Gallery"}</h2>
                        <div className={`grid grid-cols-2 ${galCols} gap-4`}>
                            {(content.images || []).map((img, i) => (
                                <div key={i} className="overflow-hidden shadow-sm" style={{ borderRadius: radius }}>
                                    <img 
                                        src={img.url} 
                                        alt="Gallery" 
                                        className={`w-full ${aspectClass} object-cover hover:scale-105 transition duration-500 cursor-pointer`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );
        // ADDED CAROUSEL RENDERER
        case 'carousel':
            return (
                <div className={`${paddingClass} overflow-hidden`} style={sectionStyle}>
                    <div className="container mx-auto px-6 max-w-7xl">
                        {content.heading && <h2 className="text-3xl font-bold text-center mb-10" style={{ color: content.textColor ? 'inherit' : globalTheme.primaryColor }}>{content.heading}</h2>}
                        {content.images && content.images.length > 0 ? (
                            <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 sm:gap-6 pb-6 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                {content.images.map((imgUrl, i) => (
                                    <div key={i} className="flex-none w-[80%] sm:w-[50%] md:w-[40%] lg:w-[30%] snap-center shadow-lg overflow-hidden relative group" style={{ borderRadius: radius }}>
                                        <img src={imgUrl} alt={`Carousel item ${i + 1}`} className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-105" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center opacity-70 border-2 border-dashed border-gray-400/50 rounded-xl p-10" style={{ borderRadius: radius }}>
                                No images added to this carousel yet.
                            </div>
                        )}
                    </div>
                </div>
            );
        case 'video':
            return (
                <div className={`${paddingClass}`} style={sectionStyle}>
                    <div className="container mx-auto px-6 max-w-5xl">
                        {content.heading && <h2 className="text-3xl font-bold text-center mb-10" style={{ color: content.textColor ? 'inherit' : globalTheme.primaryColor }}>{content.heading}</h2>}
                        {content.embedCode ? (
                            <div className="w-full overflow-hidden shadow-xl flex justify-center [&>iframe]:w-full [&>iframe]:aspect-video [&>iframe]:h-auto" style={{ borderRadius: radius }} dangerouslySetInnerHTML={{ __html: content.embedCode }} />
                        ) : (
                            <div className="relative w-full overflow-hidden shadow-xl aspect-video" style={{ borderRadius: radius }}>
                                {content.videoUrl ? (
                                    <iframe 
                                        className="absolute inset-0 w-full h-full border-0" 
                                        src={content.videoUrl.replace("watch?v=", "embed/").replace("youtu.be/", "www.youtube.com/embed/")} 
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                        allowFullScreen
                                    ></iframe>
                                ) : (
                                    <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-500 font-medium border-2 border-dashed border-gray-400" style={{ borderRadius: radius }}>
                                        Video URL or Embed Code not set
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            );
        case 'reviews':
            return (
                <div className={`${paddingClass}`} style={sectionStyle}>
                    <div className="container mx-auto px-6 max-w-6xl">
                        <h2 className="text-3xl font-bold text-center mb-10" style={{ color: content.textColor ? 'inherit' : globalTheme.primaryColor }}>{content.heading || "Client Love"}</h2>
                        {reviews && reviews.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {reviews.slice(0, 3).map(review => (
                                    <div key={review.id} className="bg-white/5 p-6 shadow-sm border border-gray-200/50 backdrop-blur-sm flex flex-col" style={{ borderRadius: radius }}>
                                        <div className="flex text-yellow-400 mb-3">
                                            {[...Array(5)].map((_, i) => (
                                                <svg key={i} className={`w-5 h-5 ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                                            ))}
                                        </div>
                                        <p className="italic mb-4 opacity-90 flex-grow">"{review.text}"</p>
                                        <p className="font-bold text-sm opacity-80 mt-auto">- {review.renterName || 'Happy Client'}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-center opacity-70">No reviews yet.</p>
                        )}
                    </div>
                </div>
            );
        case 'faq':
            return (
                <div className={`${paddingClass}`} style={sectionStyle}>
                    <div className="container mx-auto px-6 max-w-3xl">
                        <h2 className="text-3xl font-bold text-center mb-10" style={{ color: content.textColor ? 'inherit' : globalTheme.primaryColor }}>{content.heading || "FAQ"}</h2>
                        <div className="space-y-4">
                            {(content.faqs || []).map((faq, i) => (
                                <FaqAccordion key={i} faq={faq} />
                            ))}
                        </div>
                    </div>
                </div>
            );
        case 'contact':
            return (
                <div className={`${paddingClass}`} style={{ ...sectionStyle, backgroundColor: content.bgColor || globalTheme.primaryColor, color: content.textColor || '#ffffff' }}>
                    <div className="container mx-auto px-6 max-w-4xl text-center">
                        <h2 className="text-3xl font-bold mb-6">{content.heading || "Ready to Book?"}</h2>
                        <p className="text-xl mb-8 max-w-2xl mx-auto opacity-80">{content.text || "Contact us to discuss your event details and get a custom quote."}</p>
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <button onClick={startChat} className={globalTheme.buttonStyle === 'outline' ? "border-2 border-white text-white hover:bg-white/10 transition shadow-sm px-8 py-3 font-bold text-lg" : "bg-white text-gray-900 px-8 py-3 font-bold shadow-lg transition hover:bg-gray-100"} style={{ borderRadius: radius, color: globalTheme.buttonStyle === 'outline' ? 'white' : globalTheme.primaryColor }}>
                                Message Us
                            </button>
                            {vendor?.businessInfo?.website && (
                                <a href={vendor.businessInfo.website} target="_blank" rel="noopener noreferrer" className="bg-transparent border border-white/50 hover:border-white px-8 py-3 font-bold hover:bg-white/10 transition" style={{ borderRadius: radius }}>
                                    Visit Website
                                </a>
                            )}
                        </div>
                        
                        {(content.instagramUrl || content.facebookUrl || content.tiktokUrl) && (
                            <div className="flex justify-center items-center gap-6 mt-12 pt-8 border-t border-white/20">
                                {content.instagramUrl && <a href={content.instagramUrl} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition font-medium flex items-center gap-2">📸 Instagram</a>}
                                {content.facebookUrl && <a href={content.facebookUrl} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition font-medium flex items-center gap-2">📘 Facebook</a>}
                                {content.tiktokUrl && <a href={content.tiktokUrl} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition font-medium flex items-center gap-2">🎵 TikTok</a>}
                            </div>
                        )}
                    </div>
                </div>
            );
        default:
            return null;
    }
}

// ==========================================
// MAIN EDITOR COMPONENT
// ==========================================

export default function StorefrontEditor({ user, listings, reviews, onClose, db, storage }) {
    const toast = useToast();
    const defaultTheme = { primaryColor: user.themeColor || '#ec4899', backgroundColor: '#ffffff', fontFamily: user.storefront?.fontFamily || "'Inter', sans-serif", borderRadius: '0.5rem', buttonStyle: 'solid' };
    
    const defaultSections = [
        { id: 'hero', type: 'hero', enabled: true, content: { title: user.vendorName || "Welcome to my Store", subtitle: user.description || "Premium Event Rentals", imageUrl: user.coverPhotoUrl || "", buttonText: "Browse Inventory", buttonAction: "scroll" } },
        { id: 'about', type: 'text-image', enabled: true, content: { heading: "About Us", text: user.description || "We are a local rental company...", imageUrl: user.profilePicUrl || "", layout: "left" } },
        { id: 'inventory', type: 'inventory', enabled: true, content: { heading: "Our Collection" } },
        { id: 'gallery', type: 'gallery', enabled: true, content: { heading: "Photo Gallery", images: user.galleryImages || [] } },
        { id: 'video', type: 'video', enabled: true, content: { heading: "Watch Us in Action", videoUrl: "" } },
        { id: 'reviews', type: 'reviews', enabled: true, content: { heading: "Client Love" } },
        { id: 'faq', type: 'faq', enabled: true, content: { heading: "Common Questions", faqs: user.faqs || [] } },
        { id: 'contact', type: 'contact', enabled: true, content: { heading: "Let's Party!", text: "Reach out to check availability." } }
    ];

    const [sections, setSections] = useState(user.storefront?.sections || defaultSections);
    const [theme, setTheme] = useState({ ...defaultTheme, ...(user.storefront?.theme || {}) });
    const [activeSectionId, setActiveSectionId] = useState(sections[0].id);
    const [saving, setSaving] = useState(false);
    const [isAutoSaving, setIsAutoSaving] = useState(false);
    const [isStorefrontEnabled, setIsStorefrontEnabled] = useState(user.storefront?.enabled !== false);
    
    const [previewDevice, setPreviewDevice] = useState('desktop'); 
    const [editorTab, setEditorTab] = useState('content'); 
    const [templateToApply, setTemplateToApply] = useState(null); 
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [mobileView, setMobileView] = useState('preview'); 

    const [past, setPast] = useState([]);
    const [future, setFuture] = useState([]);

    const autoSaveTimeoutRef = useRef(null);
    const initialLoadRef = useRef(true);

    useEffect(() => {
        if (activeSectionId && mobileView !== 'editor') {
            const el = document.getElementById(`storefront-preview-${activeSectionId}`);
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
            }
        }
    }, [activeSectionId, mobileView]);

    useEffect(() => {
        if (!user.storefront?.sections) {
            setSections(prev => prev.map(sec => {
                if (sec.id === 'gallery' && user.galleryImages) sec.content.images = user.galleryImages;
                if (sec.id === 'faq' && user.faqs) sec.content.faqs = user.faqs;
                return sec;
            }));
        }
    }, [user]);

    useEffect(() => {
        if (initialLoadRef.current) {
            initialLoadRef.current = false;
            return;
        }
        
        if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
        
        autoSaveTimeoutRef.current = setTimeout(async () => {
            setIsAutoSaving(true);
            try {
                await updateDoc(doc(db, "users", user.uid), {
                    storefront: { sections, theme, enabled: isStorefrontEnabled, updatedAt: serverTimestamp() }
                });
            } catch (err) {
                console.error("Auto-save error:", err);
            } finally {
                setIsAutoSaving(false);
            }
        }, 3000); 
        
        return () => clearTimeout(autoSaveTimeoutRef.current);
    }, [sections, theme, db, user.uid, isStorefrontEnabled]);

    const updateDesign = (newSections, newTheme) => {
        setPast(prev => [...prev, { sections, theme }]);
        setFuture([]);
        setSections(newSections);
        setTheme(newTheme);
    };

    const handleUndo = () => {
        if (past.length === 0) return;
        const previous = past[past.length - 1];
        setFuture(prev => [{ sections, theme }, ...prev]);
        setPast(prev => prev.slice(0, -1));
        setSections(previous.sections);
        setTheme(previous.theme);
    };

    const handleRedo = () => {
        if (future.length === 0) return;
        const next = future[0];
        setPast(prev => [...prev, { sections, theme }]);
        setFuture(prev => prev.slice(1));
        setSections(next.sections);
        setTheme(next.theme);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await updateDoc(doc(db, "users", user.uid), {
                storefront: { sections, theme, enabled: isStorefrontEnabled, updatedAt: serverTimestamp() }
            });
            toast.success("Storefront published!");
            onClose();
        } catch (err) {
            console.error(err);
            toast.error("Failed to save.");
        } finally {
            setSaving(false);
        }
    };

    const handleFieldChange = (sectionId, field, value) => {
        const newSections = sections.map(sec => sec.id === sectionId ? { ...sec, content: { ...sec.content, [field]: value } } : sec);
        updateDesign(newSections, theme);
    };

    const handleThemeChange = (field, value) => {
        updateDesign(sections, { ...theme, [field]: value });
    };

    const toggleSection = (id) => {
        const newSections = sections.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s);
        updateDesign(newSections, theme);
    };

    const moveSection = (index, direction) => {
        const newSections = [...sections];
        if (direction === 'up' && index > 0) {
            [newSections[index], newSections[index - 1]] = [newSections[index - 1], newSections[index]];
        } else if (direction === 'down' && index < newSections.length - 1) {
            [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
        }
        updateDesign(newSections, theme);
    };

    const duplicateSection = (id) => {
        const secToDup = sections.find(s => s.id === id);
        if (!secToDup) return;
        const newSec = JSON.parse(JSON.stringify(secToDup));
        newSec.id = `${newSec.type}_${Date.now()}`;
        const idx = sections.findIndex(s => s.id === id);
        const newSections = [...sections];
        newSections.splice(idx + 1, 0, newSec);
        updateDesign(newSections, theme);
        setActiveSectionId(newSec.id);
    };

    const deleteSection = (id) => {
        if (sections.length <= 1) {
            toast.error("You must have at least one section.");
            return;
        }
        if (window.confirm("Are you sure you want to delete this block?")) {
            const newSections = sections.filter(s => s.id !== id);
            updateDesign(newSections, theme);
            if (activeSectionId === id) setActiveSectionId(newSections[0].id);
        }
    };

    const addNewSection = (type) => {
        let content = {};
        switch(type) {
            case 'hero': content = { title: "New Hero", subtitle: "Subtitle", buttonText: "Explore" }; break;
            case 'text-image': content = { heading: "About", text: "Text...", layout: "left" }; break;
            case 'features': content = { heading: "Features", items: [{title: "Item 1", text: "Desc", icon: "✨"}] }; break;
            case 'inventory': content = { heading: "Collection" }; break;
            case 'gallery': content = { heading: "Gallery", images: [] }; break;
            // ADDED CAROUSEL CASE
            case 'carousel': content = { heading: "Image Carousel", images: [] }; break;
            case 'video': content = { heading: "Watch Us in Action", videoUrl: "" }; break;
            case 'reviews': content = { heading: "Reviews" }; break;
            case 'faq': content = { heading: "FAQ", faqs: [] }; break;
            case 'contact': content = { heading: "Contact Us", text: "Reach out!" }; break;
            default: content = { heading: "New Section" }; break;
        }
        const newSec = { id: `${type}_${Date.now()}`, type, enabled: true, content };
        updateDesign([...sections, newSec], theme);
        setActiveSectionId(newSec.id);
        setShowAddMenu(false);
    };

    const vendorTemplates = [
        {
            id: 'modern',
            name: 'Modern Minimalist',
            theme: { primaryColor: '#111827', backgroundColor: '#ffffff', fontFamily: "'Inter', sans-serif", borderRadius: '0.5rem', buttonStyle: 'solid' },
            sections: [
                { id: 'hero_1', type: 'hero', enabled: true, content: { title: user.vendorName || "Premium Rentals", subtitle: "Elevate your next event.", imageUrl: user.coverPhotoUrl || "https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg", buttonText: "View Inventory", buttonAction: "scroll", alignment: "left", overlayOpacity: 0.4, height: "600px" } },
                { id: 'about_1', type: 'text-image', enabled: true, content: { heading: "About Us", text: user.description || "We provide high-quality event rentals designed to make your special day completely unforgettable. With a curated collection of modern pieces, we handle the logistics so you can focus on the celebration.", imageUrl: user.profilePicUrl || "https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg", layout: "left", padding: "py-24" } },
                { id: 'inventory_1', type: 'inventory', enabled: true, content: { heading: "Our Collection", backgroundColor: "#f9fafb", padding: "py-24" } },
                { id: 'reviews_1', type: 'reviews', enabled: true, content: { heading: "Client Love", padding: "py-24" } },
                { id: 'contact_1', type: 'contact', enabled: true, content: { heading: "Ready to Book?", text: "Contact us to check availability for your event date.", backgroundColor: "#111827", textColor: "#ffffff", padding: "py-24" } }
            ]
        },
        {
            id: 'playful',
            name: 'Playful Party',
            theme: { primaryColor: '#ec4899', backgroundColor: '#fdf2f8', fontFamily: "'Lato', sans-serif", borderRadius: '1.5rem', buttonStyle: 'solid' },
            sections: [
                { id: 'hero_2', type: 'hero', enabled: true, content: { title: user.vendorName || "Party Starters", subtitle: "Everything you need for a good time.", imageUrl: user.coverPhotoUrl || "https://images.pexels.com/photos/3171837/pexels-photo-3171837.jpeg", buttonText: "Let's Party", buttonAction: "scroll", alignment: "center", overlayOpacity: 0.6, height: "100vh" } },
                { id: 'features_2', type: 'features', enabled: true, content: { heading: "Why Choose Us", items: [{title: "Top Quality", text: "Premium party items.", icon: "💎"}, {title: "Reliable", text: "On-time delivery, every time.", icon: "🚚"}, {title: "Friendly Support", text: "We're here to help.", icon: "🤝"}], padding: "py-24" } },
                { id: 'inventory_2', type: 'inventory', enabled: true, content: { heading: "Rentals", backgroundColor: "#ffffff", padding: "py-24" } },
                { id: 'contact_2', type: 'contact', enabled: true, content: { heading: "Message Us", text: "Let's make it happen.", backgroundColor: "#ec4899", textColor: "#ffffff", padding: "py-24" } }
            ]
        },
        {
            id: 'elegant',
            name: 'Classic Elegance',
            theme: { primaryColor: '#d4af37', backgroundColor: '#faf9f6', fontFamily: "'Playfair Display', serif", borderRadius: '0px', buttonStyle: 'outline' },
            sections: [
                { id: 'hero_3', type: 'hero', enabled: true, content: { title: user.vendorName || "Elegant Affairs", subtitle: "Sophisticated rentals for timeless events.", imageUrl: user.coverPhotoUrl || "https://images.pexels.com/photos/256737/pexels-photo-256737.jpeg", buttonText: "Discover", buttonAction: "scroll", alignment: "center", overlayOpacity: 0.3, height: "600px" } },
                { id: 'inventory_3', type: 'inventory', enabled: true, content: { heading: "The Collection", padding: "py-24" } },
                { id: 'gallery_3', type: 'gallery', enabled: true, content: { heading: "Inspiration", images: user.galleryImages || [], backgroundColor: "#ffffff", padding: "py-24" } },
                { id: 'faq_3', type: 'faq', enabled: true, content: { heading: "FAQ", faqs: user.faqs || [], padding: "py-24" } },
                { id: 'contact_3', type: 'contact', enabled: true, content: { heading: "Inquiries", text: "Reach out for custom quotes.", backgroundColor: "#d4af37", textColor: "#ffffff", padding: "py-24" } }
            ]
        }
    ];

    const handleTemplateClick = (template) => setTemplateToApply(template);

    const confirmApplyTemplate = () => {
        if (!templateToApply) return;
        const clonedSections = JSON.parse(JSON.stringify(templateToApply.sections));
        updateDesign(clonedSections, templateToApply.theme);
        setActiveSectionId(clonedSections[0].id);
        setTemplateToApply(null);
    };

    const activeSection = sections.find(s => s.id === activeSectionId);

    const renderCommonStylesEditor = () => (
        <div className="border-t border-gray-200 pt-5 mt-5 space-y-4">
            <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Section Styling</h5>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Background Color</label>
                    <div className="flex items-center gap-2">
                        <input type="color" value={activeSection.content.backgroundColor || '#ffffff'} onChange={e => handleFieldChange(activeSection.id, 'backgroundColor', e.target.value)} className="w-8 h-8 p-0 border-0 rounded cursor-pointer" />
                        <button onClick={() => handleFieldChange(activeSection.id, 'backgroundColor', '')} className="text-xs text-gray-500 hover:text-red-500 transition">Clear</button>
                    </div>
                </div>
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Text Color</label>
                    <div className="flex items-center gap-2">
                        <input type="color" value={activeSection.content.textColor || '#000000'} onChange={e => handleFieldChange(activeSection.id, 'textColor', e.target.value)} className="w-8 h-8 p-0 border-0 rounded cursor-pointer" />
                        <button onClick={() => handleFieldChange(activeSection.id, 'textColor', '')} className="text-xs text-gray-500 hover:text-red-500 transition">Clear</button>
                    </div>
                </div>
            </div>
            <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Padding (Spacing)</label>
                <select value={activeSection.content.padding || 'py-16'} onChange={e => handleFieldChange(activeSection.id, 'padding', e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white">
                    <option value="py-8">Small (Compact)</option>
                    <option value="py-16">Medium (Default)</option>
                    <option value="py-24">Large (Spacious)</option>
                    <option value="py-32">X-Large (Very Spacious)</option>
                </select>
            </div>
            
            {/* Background Image Controls for all sections */}
            <div className="border-t border-gray-100 pt-4 mt-4">
                <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-3">Section Background Image (Optional)</h5>
                <StorefrontImageUpload onUploadComplete={(url) => handleFieldChange(activeSection.id, 'bgImageUrl', url)} storage={storage} userId={user.uid} path="backgrounds" isFullWidth={true} />
                
                {activeSection.content.bgImageUrl && (
                    <div className="grid grid-cols-2 gap-4 mt-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <div className="col-span-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Darken Overlay</label>
                            <input type="range" min="0" max="0.9" step="0.1" value={activeSection.content.bgOverlayOpacity || 0} onChange={e => handleFieldChange(activeSection.id, 'bgOverlayOpacity', parseFloat(e.target.value))} className="w-full cursor-pointer" />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer mt-2 col-span-2">
                            <input type="checkbox" checked={activeSection.content.bgParallax || false} onChange={e => handleFieldChange(activeSection.id, 'bgParallax', e.target.checked)} className="rounded text-pink-600 focus:ring-pink-500" />
                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Enable Parallax Effect</span>
                        </label>
                        <button onClick={() => { handleFieldChange(activeSection.id, 'bgImageUrl', ''); handleFieldChange(activeSection.id, 'bgOverlayOpacity', 0); }} className="col-span-2 text-xs text-red-500 font-bold hover:underline text-left mt-2">✕ Remove Image</button>
                    </div>
                )}
            </div>
        </div>
    );

    const renderEditorInputs = () => {
        if (!activeSection) return null;

        return (
            <div className="space-y-4 p-5 animate-fade-in">
                <div className="flex justify-between items-center border-b border-gray-200 pb-3">
                    <h4 className="font-bold text-lg text-gray-900 capitalize">{activeSection.type.replace('-', ' ')} Content</h4>
                    <label className="flex items-center text-sm gap-2 cursor-pointer bg-gray-100 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition">
                        <input type="checkbox" checked={activeSection.enabled} onChange={() => toggleSection(activeSection.id)} className="w-4 h-4 text-pink-600 rounded focus:ring-pink-500" />
                        <span className="font-semibold text-gray-700">Visible</span>
                    </label>
                </div>

                {activeSection.type === 'hero' && (
                    <>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Headline</label><input type="text" value={activeSection.content.title} onChange={e => handleFieldChange(activeSection.id, 'title', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" /></div>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Subtitle</label><input type="text" value={activeSection.content.subtitle} onChange={e => handleFieldChange(activeSection.id, 'subtitle', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" /></div>
                        <div>
                            <div className="flex justify-between items-end mb-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Background Image</label>
                                <StorefrontImageUpload onUploadComplete={(url) => handleFieldChange(activeSection.id, 'imageUrl', url)} storage={storage} userId={user.uid} path="hero" />
                            </div>
                            <input type="text" value={activeSection.content.imageUrl} onChange={e => handleFieldChange(activeSection.id, 'imageUrl', e.target.value)} placeholder="Or paste image URL here" className="w-full p-2.5 border rounded-lg" />
                        </div>
                        <div>
                            <div className="flex justify-between items-end mb-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Background Video (.mp4)</label>
                                <StorefrontVideoUpload onUploadComplete={(url) => handleFieldChange(activeSection.id, 'bgVideoUrl', url)} storage={storage} userId={user.uid} path="hero_video" />
                            </div>
                            <input type="text" value={activeSection.content.bgVideoUrl || ''} onChange={e => handleFieldChange(activeSection.id, 'bgVideoUrl', e.target.value)} placeholder="Or paste video URL here" className="w-full p-2.5 border rounded-lg mt-1" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Button Text</label><input type="text" value={activeSection.content.buttonText} onChange={e => handleFieldChange(activeSection.id, 'buttonText', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" /></div>
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Button Action</label>
                                <select value={activeSection.content.buttonAction} onChange={e => handleFieldChange(activeSection.id, 'buttonAction', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1 bg-white">
                                    <option value="scroll">Scroll to Items</option>
                                    <option value="chat">Open Contact</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Alignment</label>
                                <select value={activeSection.content.alignment || 'center'} onChange={e => handleFieldChange(activeSection.id, 'alignment', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1 bg-white">
                                    <option value="center">Center</option>
                                    <option value="left">Left</option>
                                    <option value="right">Right</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Overlay Darken</label>
                                <input type="range" min="0" max="1" step="0.1" value={activeSection.content.overlayOpacity !== undefined ? activeSection.content.overlayOpacity : 0.4} onChange={e => handleFieldChange(activeSection.id, 'overlayOpacity', parseFloat(e.target.value))} className="w-full mt-4 cursor-pointer" />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Section Height</label>
                            <select value={activeSection.content.height || '600px'} onChange={e => handleFieldChange(activeSection.id, 'height', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1 bg-white">
                                <option value="400px">Small (400px)</option>
                                <option value="600px">Medium (600px)</option>
                                <option value="800px">Large (800px)</option>
                                <option value="100vh">Full Screen (100vh)</option>
                            </select>
                        </div>
                    </>
                )}

                {activeSection.type === 'text-image' && (
                    <>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Heading</label><input type="text" value={activeSection.content.heading} onChange={e => handleFieldChange(activeSection.id, 'heading', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" /></div>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Text Content</label><textarea value={activeSection.content.text} onChange={e => handleFieldChange(activeSection.id, 'text', e.target.value)} className="w-full p-2.5 border rounded-lg h-32 resize-none mt-1" /></div>
                        <div>
                            <div className="flex justify-between items-end mb-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Image</label>
                                <StorefrontImageUpload onUploadComplete={(url) => handleFieldChange(activeSection.id, 'imageUrl', url)} storage={storage} userId={user.uid} path="about" />
                            </div>
                            <input type="text" value={activeSection.content.imageUrl} onChange={e => handleFieldChange(activeSection.id, 'imageUrl', e.target.value)} placeholder="Or paste image URL here" className="w-full p-2.5 border rounded-lg" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Image Position</label>
                            <select value={activeSection.content.layout} onChange={e => handleFieldChange(activeSection.id, 'layout', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1 bg-white">
                                <option value="left">Image Left</option><option value="right">Image Right</option>
                            </select>
                        </div>
                        {renderCommonStylesEditor()}
                    </>
                )}

                {activeSection.type === 'features' && (
                    <>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Heading</label><input type="text" value={activeSection.content.heading} onChange={e => handleFieldChange(activeSection.id, 'heading', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" /></div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Grid Columns</label>
                            <select value={activeSection.content.columns || '3'} onChange={e => handleFieldChange(activeSection.id, 'columns', e.target.value)} className="w-full p-2 border rounded-lg text-sm bg-white">
                                <option value="2">2 Columns</option>
                                <option value="3">3 Columns</option>
                                <option value="4">4 Columns</option>
                            </select>
                        </div>
                        <div className="space-y-4 border-t border-gray-100 pt-4">
                            <label className="text-sm font-bold text-gray-900">Manage Features</label>
                            {(activeSection.content.items || [{title: "Service", text: "Desc", icon: "✨"}, {title: "Quality", text: "Desc", icon: "💎"}, {title: "Reliable", text: "Desc", icon: "🤝"}]).map((item, i) => (
                                <div key={i} className="border p-3 rounded-lg bg-gray-50 relative group">
                                    <div className="flex gap-2 mb-2">
                                        <input type="text" placeholder="Icon (Emoji)" value={item.icon} onChange={e => {
                                            const newItems = [...activeSection.content.items];
                                            newItems[i].icon = e.target.value; handleFieldChange(activeSection.id, 'items', newItems);
                                        }} className="w-12 p-2 border rounded text-center text-lg" />
                                        <input type="text" placeholder="Title" value={item.title} onChange={e => {
                                            const newItems = [...activeSection.content.items];
                                            newItems[i].title = e.target.value; handleFieldChange(activeSection.id, 'items', newItems);
                                        }} className="flex-1 p-2 border rounded text-sm" />
                                    </div>
                                    <textarea placeholder="Description" value={item.text} onChange={e => {
                                        const newItems = [...activeSection.content.items];
                                        newItems[i].text = e.target.value; handleFieldChange(activeSection.id, 'items', newItems);
                                    }} className="w-full p-2 border rounded text-sm h-16 resize-none" />
                                </div>
                            ))}
                        </div>
                        {renderCommonStylesEditor()}
                    </>
                )}

                {/* ADDED CAROUSEL EDITOR INPUTS */}
                {activeSection.type === 'carousel' && (
                    <>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Section Heading</label>
                            <input type="text" value={activeSection.content.heading || ''} onChange={e => handleFieldChange(activeSection.id, 'heading', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" />
                        </div>
                        <StorefrontCarouselSection 
                            images={activeSection.content.images || []}
                            onUpdateImages={(newImages) => handleFieldChange(activeSection.id, 'images', newImages)}
                            storage={storage}
                            userId={user.uid}
                        />
                        {renderCommonStylesEditor()}
                    </>
                )}

                {['inventory', 'gallery', 'reviews', 'faq'].includes(activeSection.type) && (
                    <>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Section Heading</label><input type="text" value={activeSection.content.heading} onChange={e => handleFieldChange(activeSection.id, 'heading', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" /></div>
                        
                        {activeSection.type === 'gallery' && (
                            <div className="border-t pt-4 mt-4 space-y-3">
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Columns</label>
                                        <select value={activeSection.content.columns || '4'} onChange={e => handleFieldChange(activeSection.id, 'columns', e.target.value)} className="w-full p-2 border rounded-lg text-sm bg-white">
                                            <option value="2">2 Columns</option>
                                            <option value="3">3 Columns</option>
                                            <option value="4">4 Columns</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Image Shape</label>
                                        <select value={activeSection.content.aspectRatio || 'square'} onChange={e => handleFieldChange(activeSection.id, 'aspectRatio', e.target.value)} className="w-full p-2 border rounded-lg text-sm bg-white">
                                            <option value="square">Square (1:1)</option>
                                            <option value="portrait">Portrait (3:4)</option>
                                            <option value="video">Landscape (16:9)</option>
                                        </select>
                                    </div>
                                </div>
                                <label className="text-sm font-bold text-gray-900 block border-t border-gray-100 pt-4">Portfolio Images</label>
                                {(activeSection.content.images || []).map((img, idx) => (
                                    <div key={idx} className="flex gap-2 items-center bg-gray-50 p-2 rounded-lg border border-gray-200">
                                        <img src={img.url || 'https://placehold.co/40'} className="w-10 h-10 object-cover rounded bg-gray-200" alt="thumb"/>
                                        <div className="flex-1">
                                            <input type="text" value={img.url} onChange={e => { const newImgs = [...activeSection.content.images]; newImgs[idx].url = e.target.value; handleFieldChange(activeSection.id, 'images', newImgs); }} placeholder="Image URL" className="w-full p-2 border rounded text-xs" />
                                        </div>
                                        <button onClick={() => { const newImgs = [...activeSection.content.images]; newImgs.splice(idx, 1); handleFieldChange(activeSection.id, 'images', newImgs); }} className="p-2 text-gray-400 hover:text-red-500 font-bold">✕</button>
                                    </div>
                                ))}
                                <div className="flex gap-2">
                                    <button onClick={() => handleFieldChange(activeSection.id, 'images', [...(activeSection.content.images || []), { url: '' }])} className="flex-1 bg-white border border-gray-300 py-2 rounded-lg font-bold text-gray-600 hover:text-pink-600 hover:border-pink-300 transition text-sm shadow-sm">
                                        + Paste Link
                                    </button>
                                    <div className="flex-1">
                                        <StorefrontImageUpload onUploadComplete={(url) => handleFieldChange(activeSection.id, 'images', [...(activeSection.content.images || []), { url }])} storage={storage} userId={user.uid} path="gallery" isFullWidth={true} />
                                    </div>
                                </div>
                            </div>
                        )}
                        {activeSection.type === 'faq' && (
                            <div className="border-t pt-4 mt-4 space-y-3">
                                <label className="text-sm font-bold text-gray-900 block">Questions</label>
                                {(activeSection.content.faqs || []).map((faq, idx) => (
                                    <div key={idx} className="bg-gray-50 p-3 rounded-lg border border-gray-200 space-y-2 relative">
                                        <button onClick={() => { const newFaqs = [...activeSection.content.faqs]; newFaqs.splice(idx, 1); handleFieldChange(activeSection.id, 'faqs', newFaqs); }} className="absolute top-2 right-2 text-red-500 font-bold p-1">✕</button>
                                        <input type="text" value={faq.question} onChange={e => { const newFaqs = [...activeSection.content.faqs]; newFaqs[idx].question = e.target.value; handleFieldChange(activeSection.id, 'faqs', newFaqs); }} placeholder="Question" className="w-full p-2 border rounded text-sm pr-8" />
                                        <textarea value={faq.answer} onChange={e => { const newFaqs = [...activeSection.content.faqs]; newFaqs[idx].answer = e.target.value; handleFieldChange(activeSection.id, 'faqs', newFaqs); }} placeholder="Answer" className="w-full p-2 border rounded text-sm resize-none h-16" />
                                    </div>
                                ))}
                                <button onClick={() => handleFieldChange(activeSection.id, 'faqs', [...(activeSection.content.faqs || []), { question: '', answer: '' }])} className="w-full bg-white border-2 border-dashed border-gray-300 py-3 rounded-xl font-bold text-gray-500 hover:text-pink-600 hover:border-pink-300 transition">
                                    + Add FAQ
                                </button>
                            </div>
                        )}
                        {renderCommonStylesEditor()}
                    </>
                )}

                {activeSection.type === 'video' && (
                    <>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Section Heading (Optional)</label><input type="text" value={activeSection.content.heading || ''} onChange={e => handleFieldChange(activeSection.id, 'heading', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" placeholder="e.g. Watch Us in Action" /></div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">YouTube / Vimeo Link</label>
                            <input type="url" value={activeSection.content.videoUrl || ''} onChange={e => handleFieldChange(activeSection.id, 'videoUrl', e.target.value)} placeholder="https://www.youtube.com/watch?v=..." className="w-full p-2.5 border rounded-lg mt-1 mb-4" />
                            
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">OR Custom Embed Code</label>
                            <textarea value={activeSection.content.embedCode || ''} onChange={e => handleFieldChange(activeSection.id, 'embedCode', e.target.value)} placeholder='<iframe src="..."></iframe>' className="w-full p-2.5 border rounded-lg h-24 resize-none mt-1 font-mono text-xs" />
                        </div>
                        {renderCommonStylesEditor()}
                    </>
                )}

                {activeSection.type === 'contact' && (
                    <>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Heading</label><input type="text" value={activeSection.content.heading} onChange={e => handleFieldChange(activeSection.id, 'heading', e.target.value)} className="w-full p-2.5 border rounded-lg mt-1" /></div>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Message</label><textarea value={activeSection.content.text} onChange={e => handleFieldChange(activeSection.id, 'text', e.target.value)} className="w-full p-2.5 border rounded-lg h-24 resize-none mt-1" /></div>
                        <div className="border-t border-gray-100 pt-4 mt-4 space-y-3">
                            <label className="text-sm font-bold text-gray-900 block">Social Media Links</label>
                            <div><label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Instagram URL</label><input type="url" value={activeSection.content.instagramUrl || ''} onChange={e => handleFieldChange(activeSection.id, 'instagramUrl', e.target.value)} placeholder="https://instagram.com/..." className="w-full p-2 border rounded-lg mt-1 text-sm" /></div>
                            <div><label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Facebook URL</label><input type="url" value={activeSection.content.facebookUrl || ''} onChange={e => handleFieldChange(activeSection.id, 'facebookUrl', e.target.value)} placeholder="https://facebook.com/..." className="w-full p-2 border rounded-lg mt-1 text-sm" /></div>
                            <div><label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">TikTok URL</label><input type="url" value={activeSection.content.tiktokUrl || ''} onChange={e => handleFieldChange(activeSection.id, 'tiktokUrl', e.target.value)} placeholder="https://tiktok.com/@..." className="w-full p-2 border rounded-lg mt-1 text-sm" /></div>
                        </div>
                        {renderCommonStylesEditor()}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-white z-[200] flex flex-col font-sans h-screen">
            <div className="lg:hidden fixed bottom-6 left-1/2 transform -translate-x-1/2 z-[250] bg-gray-900 p-1 rounded-full flex shadow-2xl border border-gray-700 w-auto">
                <button onClick={() => setMobileView('editor')} className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${mobileView === 'editor' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-300'}`}>
                    Layout
                </button>
                <button onClick={() => setMobileView('preview')} className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${mobileView === 'preview' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-300'}`}>
                    Website
                </button>
            </div>

            {templateToApply && (
                <div className="fixed inset-0 bg-black/60 z-[300] flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-sm">
                        <h3 className="text-xl font-bold mb-4 text-gray-900">Apply Template?</h3>
                        <p className="mb-6 text-gray-600 text-sm">Applying the <strong>{templateToApply.name}</strong> template will replace your current design and sections. Are you sure you want to continue?</p>
                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                            <button onClick={() => setTemplateToApply(null)} className="px-5 py-2.5 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg transition">Cancel</button>
                            <button onClick={confirmApplyTemplate} className="px-5 py-2.5 bg-pink-600 text-white rounded-lg font-bold hover:bg-pink-700 transition shadow-md">Apply Template</button>
                        </div>
                    </div>
                </div>
            )}
            
            <header className="bg-white border-b border-gray-200 p-3 flex justify-between items-center flex-shrink-0 z-20">
                <div className="flex items-center gap-3">
                    <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition font-bold text-lg leading-none">&times;</button>
                    <div className="h-6 w-px bg-gray-300 hidden sm:block"></div>
                    <h2 className="font-bold text-gray-900 text-lg hidden sm:block">Storefront Editor</h2>
                </div>
                
                <div className="flex items-center space-x-2 md:space-x-4">
                    <div className="hidden sm:flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${!isStorefrontEnabled ? 'text-pink-600' : 'text-gray-400'}`}>Classic Grid</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                                type="checkbox" 
                                checked={isStorefrontEnabled} 
                                onChange={async (e) => {
                                    const newValue = e.target.checked;
                                    setIsStorefrontEnabled(newValue);
                                    try {
                                        await updateDoc(doc(db, "users", user.uid), { "storefront.enabled": newValue });
                                        toast.success(newValue ? "Custom storefront enabled!" : "Switched to classic grid.");
                                    } catch (err) {
                                        console.error(err);
                                    }
                                }} 
                                className="sr-only peer" 
                            />
                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-pink-500"></div>
                        </label>
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${isStorefrontEnabled ? 'text-gray-900' : 'text-gray-400'}`}>Custom Mode</span>
                    </div>

                    {isAutoSaving && <span className="text-xs font-semibold text-gray-500 animate-pulse hidden md:block">Auto-saving...</span>}
                    <button onClick={handleSave} disabled={saving || isAutoSaving} className="bg-gray-900 text-white px-6 py-2 rounded-lg font-bold hover:bg-black transition shadow-sm disabled:bg-gray-400 flex items-center gap-2 text-sm">
                        {(saving || isAutoSaving) ? <span className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span> : '💾'} 
                        <span>{(saving || isAutoSaving) ? 'Saving...' : 'Publish'}</span>
                    </button>
                </div>
            </header>

            <div className="flex flex-grow overflow-hidden relative">
                <aside className={`w-full lg:w-[400px] flex flex-col border-r border-gray-200 bg-gray-50 z-10 shadow-[2px_0_10px_rgba(0,0,0,0.05)] flex-shrink-0 ${mobileView === 'preview' ? 'hidden lg:flex' : 'flex'}`}>
                    <div className="flex border-b border-gray-200 bg-gray-100 flex-shrink-0 p-2 gap-2">
                        <button 
                            onClick={() => setEditorTab('content')} 
                            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors shadow-sm ${editorTab === 'content' ? 'bg-white text-gray-900 border border-gray-200' : 'bg-transparent text-gray-500 hover:text-gray-800 border border-transparent hover:bg-gray-200'}`}
                        >
                            Content & Blocks
                        </button>
                        <button 
                            onClick={() => setEditorTab('design')} 
                            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors shadow-sm ${editorTab === 'design' ? 'bg-white text-gray-900 border border-gray-200' : 'bg-transparent text-gray-500 hover:text-gray-800 border border-transparent hover:bg-gray-200'}`}
                        >
                            Theme Design
                        </button>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col">
                        {editorTab === 'design' && (
                            <div className="p-5 bg-white space-y-6 animate-fade-in min-h-full">
                                <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-3">Instant Templates</label>
                                    <div className="grid grid-cols-1 gap-3">
                                        {vendorTemplates.map(tpl => (
                                            <div 
                                                key={tpl.id} 
                                                onClick={() => handleTemplateClick(tpl)}
                                                className="border border-gray-200 rounded-lg p-3 cursor-pointer hover:border-pink-300 hover:shadow-md transition group flex items-center justify-between bg-gray-50 hover:bg-white"
                                            >
                                                <div>
                                                    <p className="font-bold text-gray-900 group-hover:text-pink-600 transition" style={{ fontFamily: tpl.theme.fontFamily }}>{tpl.name}</p>
                                                    <div className="flex gap-1 mt-1.5">
                                                        <span className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: tpl.theme.primaryColor }}></span>
                                                        <span className="w-3 h-3 rounded-full shadow-sm border border-gray-200" style={{ backgroundColor: tpl.theme.backgroundColor }}></span>
                                                    </div>
                                                </div>
                                                <span className="text-xs font-semibold text-gray-500 bg-white border border-gray-200 px-2 py-1 rounded shadow-sm group-hover:text-pink-600">Apply</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="border-t border-gray-100 pt-6 space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-2">Theme Color</label>
                                            <div className="flex items-center gap-2">
                                                <input type="color" value={theme.primaryColor} onChange={e => handleThemeChange('primaryColor', e.target.value)} className="w-8 h-8 p-0 border-0 rounded cursor-pointer" />
                                                <input type="text" value={theme.primaryColor} onChange={e => handleThemeChange('primaryColor', e.target.value)} className="flex-1 p-1.5 border border-gray-300 rounded-md text-xs font-mono font-bold" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-2">Background</label>
                                            <div className="flex items-center gap-2">
                                                <input type="color" value={theme.backgroundColor} onChange={e => handleThemeChange('backgroundColor', e.target.value)} className="w-8 h-8 p-0 border-0 rounded cursor-pointer" />
                                                <input type="text" value={theme.backgroundColor} onChange={e => handleThemeChange('backgroundColor', e.target.value)} className="flex-1 p-1.5 border border-gray-300 rounded-md text-xs font-mono font-bold" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-2">Typography</label>
                                            <select value={theme.fontFamily} onChange={e => handleThemeChange('fontFamily', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-xs bg-white">
                                                <option value="'Playfair Display', serif">Playfair Display</option>
                                                <option value="'Inter', sans-serif">Inter</option>
                                                <option value="'Lato', sans-serif">Lato</option>
                                                <option value="'Georgia', serif">Georgia</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-2">Corner Radius</label>
                                            <select value={theme.borderRadius} onChange={e => handleThemeChange('borderRadius', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-xs bg-white">
                                                <option value="0px">Sharp (0px)</option>
                                                <option value="0.5rem">Rounded (8px)</option>
                                                <option value="1rem">Extra Rounded (16px)</option>
                                                <option value="9999px">Pill (Full)</option>
                                            </select>
                                        </div>
                                        <div className="col-span-2">
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-2">Button Style</label>
                                            <select value={theme.buttonStyle || 'solid'} onChange={e => handleThemeChange('buttonStyle', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-xs bg-white">
                                                <option value="solid">Solid Fill</option>
                                                <option value="outline">Outline</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {editorTab === 'content' && (
                            <div className="flex-1 flex flex-col animate-fade-in bg-white">
                                <div className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0 z-20">
                                    <div className="flex justify-between items-center mb-3">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Page Builder</p>
                                        <div className="relative">
                                            <button onClick={() => setShowAddMenu(!showAddMenu)} className="text-xs bg-white border border-gray-200 px-3 py-1.5 rounded shadow-sm hover:bg-gray-50 font-bold text-gray-800 flex items-center gap-1.5 transition">
                                                <span className="text-lg leading-none mb-0.5">+</span> Add Block
                                            </button>
                                            {showAddMenu && (
                                                <div className="absolute left-0 lg:left-auto lg:right-0 top-full mt-1 w-48 bg-white border border-gray-200 shadow-2xl rounded-lg overflow-hidden animate-fade-in">
                                                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 text-[10px] font-bold text-gray-500 uppercase">Select Block Type</div>
                                                    {/* ADDED CAROUSEL TO THIS ARRAY */}
                                                    {['hero', 'text-image', 'features', 'inventory', 'gallery', 'carousel', 'video', 'reviews', 'faq', 'contact'].map(t => (
                                                        <button key={t} onClick={() => addNewSection(t)} className="w-full text-left px-4 py-2.5 text-sm font-medium hover:bg-pink-50 hover:text-pink-600 capitalize border-b border-gray-50 last:border-0 transition">{t.replace('-', ' ')}</button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="flex gap-1 mb-3 bg-white p-1 rounded-md border border-gray-200 shadow-sm w-fit">
                                        <button onClick={handleUndo} disabled={past.length === 0} className="p-1.5 rounded hover:bg-gray-100 text-gray-600 disabled:opacity-30 transition" title="Undo">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                                        </button>
                                        <div className="w-px bg-gray-200 mx-1"></div>
                                        <button onClick={handleRedo} disabled={future.length === 0} className="p-1.5 rounded hover:bg-gray-100 text-gray-600 disabled:opacity-30 transition" title="Redo">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" /></svg>
                                        </button>
                                    </div>

                                    <div className="space-y-2 max-h-[35vh] overflow-y-auto custom-scrollbar pr-1">
                                        {sections.map((section, index) => (
                                            <div 
                                                key={section.id} 
                                                onClick={() => { setActiveSectionId(section.id); setMobileView('preview'); }}
                                                className={`p-3 rounded-lg cursor-pointer border flex flex-col gap-3 transition-all ${activeSectionId === section.id ? 'bg-white border-pink-500 shadow-md ring-1 ring-pink-500/20' : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'}`}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-4 h-4 flex items-center justify-center text-gray-400 opacity-50">⋮⋮</div>
                                                        <span className={`font-bold text-sm uppercase tracking-wide ${!section.enabled ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{section.type.replace('-', ' ')}</span>
                                                    </div>
                                                    <div className="flex gap-1 bg-gray-50 rounded p-0.5 border border-gray-200" onClick={e => e.stopPropagation()}>
                                                        <button onClick={() => moveSection(index, 'up')} disabled={index===0} className="w-6 h-6 flex items-center justify-center hover:bg-white rounded text-gray-600 font-bold disabled:opacity-30">↑</button>
                                                        <button onClick={() => moveSection(index, 'down')} disabled={index===sections.length-1} className="w-6 h-6 flex items-center justify-center hover:bg-white rounded text-gray-600 font-bold disabled:opacity-30">↓</button>
                                                    </div>
                                                </div>
                                                
                                                {activeSectionId === section.id && (
                                                    <div className="flex justify-between items-center pt-3 border-t border-gray-100 mt-1 animate-fade-in" onClick={e => e.stopPropagation()}>
                                                        <label className="flex items-center text-xs gap-1.5 cursor-pointer text-gray-600 font-medium hover:text-gray-900">
                                                            <input type="checkbox" checked={section.enabled} onChange={() => toggleSection(section.id)} className="w-3.5 h-3.5 text-pink-600 rounded focus:ring-pink-500 border-gray-300" />
                                                            Visible
                                                        </label>
                                                        <div className="flex gap-3">
                                                            <button onClick={() => duplicateSection(section.id)} className="text-gray-500 hover:text-blue-600 text-xs font-bold transition flex items-center gap-1">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg> Copy
                                                            </button>
                                                            <button onClick={() => deleteSection(section.id)} className="text-gray-500 hover:text-red-600 text-xs font-bold transition flex items-center gap-1">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg> Delete
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex-1 bg-white overflow-y-auto">
                                    {renderEditorInputs()}
                                </div>
                            </div>
                        )}
                    </div>
                </aside>

                <main className={`flex-1 bg-gray-200 overflow-hidden relative flex flex-col ${mobileView === 'editor' ? 'hidden lg:flex' : 'flex'}`}>
                    <div className="bg-gray-100 border-b border-gray-300 p-2.5 flex justify-center items-center gap-4 shadow-inner z-10 flex-shrink-0">
                        <div className="bg-white p-1 rounded-lg flex border border-gray-200 shadow-sm">
                            <button 
                                onClick={() => setPreviewDevice('desktop')} 
                                className={`px-4 py-1.5 rounded-md text-sm font-bold flex items-center gap-2 transition ${previewDevice === 'desktop' ? 'bg-gray-900 text-white shadow' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg> Desktop
                            </button>
                            <button 
                                onClick={() => setPreviewDevice('mobile')} 
                                className={`px-4 py-1.5 rounded-md text-sm font-bold flex items-center gap-2 transition ${previewDevice === 'mobile' ? 'bg-gray-900 text-white shadow' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg> Mobile
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-8 flex justify-center items-start">
                        <div className={`bg-white shadow-2xl relative transition-all duration-500 flex flex-col flex-shrink-0 ${previewDevice === 'mobile' ? 'w-[375px] rounded-[2rem] border-[12px] border-gray-900 overflow-hidden h-[812px]' : 'w-full max-w-[1400px] min-h-full'}`}>
                            {previewDevice === 'mobile' && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-gray-900 rounded-b-xl z-[60]"></div>}
                            
                            <div className="flex-1 overflow-hidden relative rounded-[1rem] md:rounded-none">
                                {previewDevice === 'mobile' ? (
                                    <IFramePreview className="w-full h-full bg-white border-none z-10 relative">
                                        <div className="min-h-full" style={{ fontFamily: theme.fontFamily, backgroundColor: theme.backgroundColor }}>
                                            {sections.map(section => {
                                                if (!section.enabled) return null;
                                                return (
                                                    <div key={section.id} id={`storefront-preview-${section.id}`}>
                                                        <StorefrontSectionRenderer 
                                                            key={section.id} 
                                                            section={section} 
                                                            listings={listings} 
                                                            reviews={reviews} 
                                                            user={null}
                                                            navigateTo={() => {}} 
                                                            vendor={user}
                                                            theme={theme}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </IFramePreview>
                                ) : (
                                    <div className="w-full h-full overflow-y-auto custom-scrollbar relative" style={{ fontFamily: theme.fontFamily, backgroundColor: theme.backgroundColor }}>
                                        {sections.map(section => {
                                            if (!section.enabled) return null;
                                            return (
                                                <div key={section.id} id={`storefront-preview-${section.id}`} className={`relative ${activeSectionId === section.id ? 'ring-inset ring-4 ring-pink-500/30' : 'hover:ring-inset hover:ring-4 hover:ring-pink-500/10'}`}>
                                                    <StorefrontSectionRenderer 
                                                        section={section} 
                                                        listings={listings} 
                                                        reviews={reviews} 
                                                        user={null}
                                                        navigateTo={() => {}} 
                                                        vendor={user}
                                                        theme={theme}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}